import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';
import { LoginForm, SignupForm } from 'src/components/molecules';
import { useCallback, useRef, useState } from 'react';
import { BLACK, WHITE } from 'src/constants/ui';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  email?: string;
  showSignupForm?: boolean;
  showSignUpOnLoad?: boolean;
  sendToAccountInfo?: boolean;
  loginFormText?: string;
  returnUrl?: string;
}

const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  email,
  loginFormText,
  showSignupForm = true,
  showSignUpOnLoad = false,
  sendToAccountInfo = true,
  returnUrl,
}) => {
  const [showSignUp, setShowSignUp] = useState(showSignUpOnLoad);
  const [isScrolled, setIsScrolled] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = useCallback((event: any) => {
    const { scrollTop } = event.target;
    setIsScrolled(scrollTop > 0);
  }, []);

  const handleFocusCapture = useCallback((event: any) => {
    const target = event.target as HTMLElement | null;
    const container = bodyRef.current;
    if (!target || !container) return;

    const isEmailInput =
      target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'email';
    if (!isEmailInput) return;

    try {
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const offsetWithinContainer = targetRect.top - containerRect.top;

      // Instantly move so Chrome autofill popover stays attached to the field
      container.scrollTop += offsetWithinContainer - 8; // small top padding
    } catch {}
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent
        bg={WHITE}
        color={BLACK}
        maxHeight={{ base: 'calc(100vh - 1rem)', md: '80vh' }}
        margin={{ base: '0.5rem', md: '1rem' }}
      >
        <ModalHeader />
        <ModalCloseButton zIndex="1" position="absolute" top="1rem" right="1rem" />
        <ModalBody
          ref={bodyRef}
          onScroll={handleScroll}
          onFocusCapture={handleFocusCapture}
          overflowY="auto"
          position="relative"
          _after={
            !isScrolled
              ? {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '20px',
                  background: 'linear-gradient(to top, #fff, rgba(255, 255, 255, 0))',
                }
              : undefined
          }
          bg="white"
        >
          {showSignUp ? (
            <SignupForm
              onClose={onClose}
              setShowSignUp={setShowSignUp}
              sendToAccountInfo={sendToAccountInfo}
              returnUrl={returnUrl}
            />
          ) : (
            <LoginForm
              userEmail={email}
              onClose={onClose}
              setShowSignUp={setShowSignUp}
              showSignupForm={showSignupForm}
              loginFormText={loginFormText}
              returnUrl={returnUrl}
            />
          )}
        </ModalBody>
        <ModalFooter />
      </ModalContent>
    </Modal>
  );
};

export default LoginModal;
